import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, Printer, Pill } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';

const MedCardsDetailsPage = () => {
  const { assignmentId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  const [assignment, setAssignment] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAssignmentDetails();
  }, [assignmentId]);

  const loadAssignmentDetails = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('dentalToken');
      
      // Get assignment details
      const assignmentResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/med-cards/assignment/${assignmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const assignmentData = assignmentResponse.data.assignment;
      setAssignment(assignmentData);
      
      // Fetch card details
      const cardDetailsPromises = assignmentData.medCardIds.map(cardId =>
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/med-cards/${cardId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      );
      
      const cardResponses = await Promise.all(cardDetailsPromises);
      const cardsData = cardResponses.map(res => res.data.data || res.data);
      setCards(cardsData);
      
    } catch (err) {
      console.error('Load error:', err);
      toast({
        title: "Error",
        description: "Failed to load Med Cards details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!assignment || cards.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Button variant="outline" onClick={() => navigate(-1)} className="mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <Card>
            <CardContent className="p-6">
              <p className="text-center text-gray-600">Med Cards assignment not found</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
          <Button onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-2xl">Med Cards Assignment</CardTitle>
            <div className="text-sm text-gray-600 mt-2">
              <div>{assignment.patientName}</div>
              <div>Assigned by {assignment.dentistName}</div>
              <div>{new Date(assignment.assignedAt).toLocaleDateString()}</div>
            </div>
          </CardHeader>
        </Card>

        <div className="space-y-6">
          {cards.map((card, index) => {
            const content = card.customContent || card.baseContent || '';
            const sections = content.split(/\n\n+/).filter(s => s.trim() && !s.toLowerCase().includes('provider notice'));
            
            return (
              <Card key={index}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl">{card.name}</CardTitle>
                      {card.category && (
                        <span className="text-sm text-gray-600">{card.category}</span>
                      )}
                    </div>
                    <Pill className="h-8 w-8 text-orange-600" />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {sections.map((section, idx) => (
                      <div key={idx} className="bg-gray-50 p-4 rounded border border-gray-200">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap leading-relaxed">
                          {section}
                        </p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MedCardsDetailsPage;
